import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	HostBinding,
	Input,
	OnDestroy,
	OnInit,
	Output,
	ViewEncapsulation,
} from "@angular/core";
import { NavigationExtras } from "@angular/router";
import { AccountAppValue, AccountAppCommand } from "@fabric-types/app-account";
import { PaymentsAppCommand, PaymentsAppValue } from "@fabric-types/app-payments";
import { select, Store } from "@ngrx/store";
import { MyAccountAction, TrackingContext } from "@obg/mat.ui.core";
import { AlternativeLayoutConfigService } from "@obg/ngx.common";
import {
	AamsBalance,
	Balance,
	CORE_ROUTE,
	CoreState,
	CoreUiInteractionEvent,
	Customer,
	Jurisdiction,
	JurisdictionSelector,
	UiInteraction,
} from "@obg/ngx.core";
import { FeatureFlagService } from "@obg/ngx.global";
import { DocumentRef } from "@obg/ngx.global.evo";
import { RouteLink, RouteAction } from "@obg/ngx.router";
import { AccordionToggleChangeArgs } from "@obg/ngx.ui.global";
import { ignoreNil } from "@obg/rxjs";
import { Observable, Subscription } from "rxjs";
import { filter, map, switchMap, tap } from "rxjs/operators";

import { KycNotification } from "../../header/kyc-notification/kyc-notification.model";
import { UserSummaryConfigService } from "../user-summary.config";

import { WithdrawalStatusFeatureFlag } from "./funds-overview.const";
import { FundsOverviewOptions } from "./funds-overview.model";

const FUNDS_INTERACTION: UiInteraction = {
	event: CoreUiInteractionEvent.Navigate,
	context: TrackingContext.MyAccountInteractionContext,
};

const DEFAULT_OPTIONS: FundsOverviewOptions = {
	hideBalance: false,
	hideWithdraw: false,
	enableAccordion: false,
	bonusVisible: true,
};

@Component({
	selector: "obg-m-funds-overview",
	templateUrl: "./funds-overview.component.html",
	styleUrls: ["./funds-overview.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class FundsOverviewComponent implements OnDestroy, OnInit {
	@HostBinding("class.obg-m-funds-overview") class = true;

	@Input() balance: Balance | AamsBalance | undefined;
	@Input() customer: Customer | undefined;
	@Input() showKycNotification: boolean | undefined;
	@Input() notificationData: KycNotification | undefined;

	@Input() get options(): FundsOverviewOptions {
		return this._options;
	}
	set options(value: FundsOverviewOptions) {
		this._options = { ...DEFAULT_OPTIONS, ...value };
		if (this._options.enableAccordion) {
			this.expandedKeys = [];
		}

		if (this.config.getWithdrawLayoutEnabled()) {
			this._options.hideWithdraw = false;
			this.isFullWidth = false;
		}
	}

	@Input() withdrawRoute: RouteLink | string | undefined;
	@Input() depositRoute: RouteLink | string | undefined;
	@Input() depositRouteOptions: NavigationExtras | undefined;
	@Input() balanceHashedEnabled = false;

	@Input() mobileHeaderV2Enabled: boolean | undefined; // TODO: AOCC-9292 remove this when mobile header v2 is released

	@Output() deposit = new EventEmitter();
	@Output() withdraw = new EventEmitter();

	depositTrackingData: UiInteraction = { ...FUNDS_INTERACTION, data: { ItemClicked: MyAccountAction.Deposit } };
	withdrawTrackingData: UiInteraction = { ...FUNDS_INTERACTION, data: { ItemClicked: MyAccountAction.Withdraw } };
	isFullWidth = true;
	isAlternativeButtonStyleEnabled: boolean | undefined;
	isAamsJurisdiction$: Observable<boolean> | undefined;
	isBalanceHashed = false;
	isExpandedAccordion = false;

	readonly fundsOverview = "funds-overview";
	expandedKeys = [this.fundsOverview];

	readonly verificationRoute = CORE_ROUTE.verification.root;

	readonly isWithdrawalStatusTrackingEnabled$ = this.featureFlagService.get$("payments_withdrawalstatus").pipe(
		filter((flag: WithdrawalStatusFeatureFlag) => !!flag && flag.enabled),
		switchMap(
			() =>
				new Observable<boolean>(this.documentRef.obgEventBus.retrieve(PaymentsAppValue.CustomerHasWithdrawals))
		),
		tap(() => {
			this.openWithdrawStatus();
		})
	);

	private _options = DEFAULT_OPTIONS;
	private data$$: Subscription | undefined;

	constructor(
		private changeDetector: ChangeDetectorRef,
		private config: UserSummaryConfigService,
		private alternativeLayoutConfig: AlternativeLayoutConfigService,
		private store: Store<CoreState>,
		private documentRef: DocumentRef,
		private featureFlagService: FeatureFlagService
	) {}

	ngOnInit() {
		this.isAlternativeButtonStyleEnabled = this.alternativeLayoutConfig.alternativeButtonsEnabled();

		this.isAamsJurisdiction$ = this.store.pipe(
			select(JurisdictionSelector.get),
			ignoreNil(),
			map((jurisdiction) => jurisdiction === Jurisdiction.Aams)
		);

		this.data$$ = new Observable(this.documentRef.obgEventBus.retrieve(AccountAppValue.IsBalanceHashed))
			.pipe(
				tap((isBalanceHashed) => {
					this.isBalanceHashed = isBalanceHashed;
				})
			)
			.subscribe(() => {
				this.changeDetector.markForCheck();
			});
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();
	}

	onToggleChange(accordion: AccordionToggleChangeArgs) {
		this.isExpandedAccordion = accordion.isExpanded;
	}

	hashBalance(event: MouseEvent, isBalanceHashed: boolean) {
		event.preventDefault();
		event.stopPropagation();
		this.documentRef.obgEventBus?.dispatch(AccountAppCommand.SaveUserSettings, {
			isBalanceHashed: !isBalanceHashed,
		});
	}

	private openWithdrawStatus() {
		this.documentRef.obgEventBus.listen(PaymentsAppCommand.OpenWithdrawalStatusTracking, () => {
			this.store.dispatch(RouteAction.navigate(CORE_ROUTE.withdrawalStatus));
		});

		this.documentRef.obgEventBus.listen(PaymentsAppCommand.WithdrawalStatusTrackingNavigateBack, () => {
			this.store.dispatch(RouteAction.navigate(CORE_ROUTE.home));
		});

		this.documentRef.obgEventBus.listen(PaymentsAppCommand.VerifyAccount, () => {
			this.store.dispatch(RouteAction.navigate(CORE_ROUTE.verification.root));
		});
	}
}
